import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

/**
 * Option-list editor for `x-uioptions` when `x-uitype` is one of:
 *   dropdown, radio, multi-select.
 *
 * Always works against a native array `[{ value, label }, ...]`. The
 * orchestrator (RenderHintsForm) is responsible for the dropdown
 * convention of round-tripping the array back as a JSON-encoded string.
 *
 * Row identity uses opaque local UUIDs (not `value`) so editing the
 * value field doesn't remount the input and drop focus on every
 * keystroke. Same focus-stability trick used by MasterDataMappingsForm.
 */

let __rowCounter = 0;
function newRowId() {
	__rowCounter += 1;
	return `rh-opt-${Date.now()}-${__rowCounter}`;
}

function arrayToRows(arr) {
	if (!Array.isArray(arr)) return [];
	return arr.map((opt) => ({
		id: newRowId(),
		value: opt && Object.prototype.hasOwnProperty.call(opt, 'value')
			? String(opt.value ?? '')
			: '',
		label: opt && Object.prototype.hasOwnProperty.call(opt, 'label')
			? String(opt.label ?? '')
			: '',
		// Preserve any extra keys the user authored beyond value/label so
		// they survive a save without edits (defensive — most production
		// shapes are exactly {value, label}).
		extras: opt && typeof opt === 'object'
			? Object.fromEntries(Object.entries(opt).filter(([k]) => k !== 'value' && k !== 'label'))
			: {},
	}));
}

function rowsToArray(rows) {
	return rows
		.filter((r) => (r.value !== '' && r.value !== undefined) || (r.label || '').trim() !== '')
		.map((r) => {
			const parsedValue = parseValueLiteral(r.value);
			return { ...r.extras, value: parsedValue, label: r.label };
		});
}

// Options sometimes use numeric values in production data
// (e.g. {value: 1, label: "Yes"}). Try to preserve numeric typing
// when the input string parses cleanly as a finite number; fall back
// to the raw string otherwise so users can still author string values.
function parseValueLiteral(raw) {
	if (raw === '' || raw === null || raw === undefined) return '';
	const trimmed = String(raw).trim();
	if (trimmed === '') return raw;
	if (/^-?\d+$/.test(trimmed)) {
		const n = Number(trimmed);
		if (Number.isFinite(n)) return n;
	}
	if (/^-?\d*\.\d+$/.test(trimmed)) {
		const n = Number(trimmed);
		if (Number.isFinite(n)) return n;
	}
	return raw;
}

export default function OptionListEditor({ value, onChange, disabled = false }) {
	const [rows, setRows] = useState(() => arrayToRows(value));
	const lastSeenValueRef = useRef(value);

	useEffect(() => {
		if (value === lastSeenValueRef.current) return;
		const ourLastEmit = rowsToArray(rows);
		const sameAsRows = JSON.stringify(ourLastEmit) === JSON.stringify(value);
		lastSeenValueRef.current = value;
		if (!sameAsRows) {
			setRows(arrayToRows(value));
		}
	}, [value, rows]);

	const commit = (nextRows) => {
		setRows(nextRows);
		const next = rowsToArray(nextRows);
		lastSeenValueRef.current = next;
		onChange(next);
	};

	const update = (id, patch) => {
		commit(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
	};

	const add = () => {
		commit([...rows, { id: newRowId(), value: '', label: '', extras: {} }]);
	};

	const remove = (id) => {
		commit(rows.filter((r) => r.id !== id));
	};

	return (
		<div className="dsv-cv-form" data-testid="rh-option-list">
			{rows.length === 0 && (
				<p className="dsv-cv-form__empty" data-testid="rh-option-list-empty">
					No options yet. Click <strong>+ Add Option</strong> to add one.
				</p>
			)}

			{rows.length > 0 && (
				<div className="dsv-cv-table dsv-cv-table--two-col" role="table">
					<div className="dsv-cv-table__head" role="row">
						<div className="dsv-cv-table__cell" role="columnheader">Value</div>
						<div className="dsv-cv-table__cell" role="columnheader">Label</div>
						<div className="dsv-cv-table__cell dsv-cv-table__cell--actions" role="columnheader" aria-label="Actions" />
					</div>
					{rows.map((row) => (
						<div key={row.id} className="dsv-cv-table__row" role="row" data-testid="rh-option-row">
							<div className="dsv-cv-table__cell" role="cell">
								<input
									className="dsv-modal__input"
									type="text"
									value={row.value}
									disabled={disabled}
									onChange={(e) => update(row.id, { value: e.target.value })}
									placeholder="e.g. 1"
									aria-label="Option value"
								/>
							</div>
							<div className="dsv-cv-table__cell" role="cell">
								<input
									className="dsv-modal__input"
									type="text"
									value={row.label}
									disabled={disabled}
									onChange={(e) => update(row.id, { label: e.target.value })}
									placeholder="e.g. Yes"
									aria-label="Option label"
								/>
							</div>
							<div className="dsv-cv-table__cell dsv-cv-table__cell--actions" role="cell">
								{!disabled && (
									<button
										type="button"
										className="dsv-cv-table__remove"
										onClick={() => remove(row.id)}
										data-testid="rh-option-remove"
										aria-label="Remove option"
										title="Remove option"
									>
										×
									</button>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			{!disabled && (
				<button
					type="button"
					className="dsv-btn dsv-btn--secondary dsv-cv-form__add"
					onClick={add}
					data-testid="rh-option-add"
				>
					+ Add Option
				</button>
			)}
		</div>
	);
}

OptionListEditor.propTypes = {
	value: PropTypes.array,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};
